<script setup>
defineProps({
  selectionData: Object,    // { items, display_fields, selection_mode, title, description }
  selectedItems: Array,
  editedItems: null, // Map instance
  newItems: Array,
  editingItem: [Number, String],
  submitting: Boolean
});

defineEmits([
  'toggle-item', 'select-all', 'clear-all',
  'start-edit', 'save-edit', 'cancel-edit',
  'add-new', 'remove-new', 'update-field',
  'submit', 'cancel'
]);

function isItemSelected(index) {
  return (Array.isArray(this?.selectedItems) ? this.selectedItems : []).includes(index);
}

function getItemData(index) {
  if (this?.editedItems?.has?.(index)) {
    return this.editedItems.get(index);
  }
  return this?.selectionData?.items?.[index] || {};
}
</script>

<script>
export default {
  methods: {
    isSelected(index) {
      return (this.selectedItems || []).includes(index);
    },
    getItem(index) {
      if (this.editedItems && this.editedItems.has && this.editedItems.has(index)) {
        return this.editedItems.get(index);
      }
      return this.selectionData?.items?.[index] || {};
    },
    getDisplayFields() {
      if (this.selectionData?.display_fields?.length > 0) {
        return this.selectionData.display_fields;
      }
      if (this.selectionData?.items?.length > 0) {
        return Object.keys(this.selectionData.items[0]);
      }
      return [];
    }
  }
};
</script>

<template>
  <div class="cr-manual-selection">
    <div class="selection-content">
      <v-icon size="64" color="orange" class="mb-4">mdi-hand-pointing-up</v-icon>
      <h2 class="selection-title">Select Items to Continue</h2>

      <v-alert type="info" outlined dense class="mb-4" style="max-width: 600px;">
        <strong>Action Required:</strong>
        {{ selectionData.selection_mode === 'single' ? 'Select one item' : 'Select items' }} to continue chain execution.
        <span v-if="selectionData.selection_mode === 'multiple'">
          ({{ selectedItems.length + newItems.length }} of {{ selectionData.items.length + newItems.length }} selected)
        </span>
        <br>
        <small>You can edit existing items using the pencil icon, or add new items using the "Add New Item" button.</small>
      </v-alert>

      <!-- Selection Controls -->
      <div v-if="selectionData.selection_mode === 'multiple'" class="selection-controls mb-3">
        <v-btn small outlined color="primary" @click="$emit('select-all')" :disabled="submitting">
          <v-icon small left>mdi-checkbox-multiple-marked</v-icon>
          Select All
        </v-btn>
        <v-btn small outlined color="grey" @click="$emit('clear-all')" :disabled="submitting" class="ml-2">
          <v-icon small left>mdi-checkbox-multiple-blank-outline</v-icon>
          Clear All
        </v-btn>
      </div>

      <!-- Items List -->
      <div class="selection-items-container" style="max-width: 800px; width: 100%;">
        <!-- Existing items -->
        <div
          v-for="(item, index) in selectionData.items"
          :key="'existing_' + index"
          :class="['selection-item', {
            'selected': isSelected(index),
            'disabled': submitting,
            'editing': editingItem === index
          }]"
        >
          <div class="selection-item-checkbox" @click="$emit('toggle-item', index)">
            <v-icon :color="isSelected(index) ? 'primary' : 'grey'">
              {{ isSelected(index) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
          </div>

          <!-- View Mode -->
          <div v-if="editingItem !== index" class="selection-item-content" @click="$emit('toggle-item', index)">
            <div v-for="field in getDisplayFields()" :key="field" class="selection-item-field">
              <strong>{{ field }}:</strong>
              <span>{{ getItem(index)[field] || 'N/A' }}</span>
            </div>
            <div v-if="getDisplayFields().length === 0">
              <pre class="selection-item-json">{{ JSON.stringify(getItem(index), null, 2) }}</pre>
            </div>
          </div>

          <!-- Edit Mode -->
          <div v-else class="selection-item-content editing-content">
            <div v-for="field in getDisplayFields()" :key="field" class="selection-item-field-edit">
              <v-text-field
                :value="getItem(index)[field]"
                :label="field"
                @input="$emit('update-field', index, field, $event)"
                outlined
                dense
                hide-details
              ></v-text-field>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="selection-item-actions">
            <v-btn
              v-if="editingItem !== index"
              icon
              x-small
              @click.stop="$emit('start-edit', index)"
              :disabled="submitting"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <template v-else>
              <v-btn icon x-small color="success" @click.stop="$emit('save-edit', index)">
                <v-icon small>mdi-check</v-icon>
              </v-btn>
              <v-btn icon x-small color="error" @click.stop="$emit('cancel-edit', index)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </template>
          </div>
        </div>

        <!-- New items added by user -->
        <div
          v-for="(newItem, newIndex) in newItems"
          :key="'new_' + newIndex"
          :class="['selection-item', 'new-item', 'selected', {
            'disabled': submitting,
            'editing': editingItem === `new_${newIndex}`
          }]"
        >
          <div class="selection-item-checkbox">
            <v-icon color="success">mdi-checkbox-marked</v-icon>
          </div>

          <div class="selection-item-content editing-content">
            <div v-for="field in getDisplayFields()" :key="field" class="selection-item-field-edit">
              <v-text-field
                :value="newItem[field]"
                :label="field"
                @input="$emit('update-field', `new_${newIndex}`, field, $event)"
                outlined
                dense
                hide-details
              ></v-text-field>
            </div>
          </div>

          <div class="selection-item-actions">
            <v-btn icon x-small color="success" @click.stop="$emit('save-edit', `new_${newIndex}`)">
              <v-icon small>mdi-check</v-icon>
            </v-btn>
            <v-btn icon x-small color="error" @click.stop="$emit('remove-new', newIndex)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Add New Item Button -->
        <div class="add-item-container mt-3">
          <v-btn outlined color="primary" @click="$emit('add-new')" :disabled="submitting">
            <v-icon left>mdi-plus-circle</v-icon>
            Add New Item
          </v-btn>
        </div>
      </div>

      <!-- Submit/Cancel Buttons -->
      <div class="selection-actions mt-4">
        <v-btn
          color="primary"
          @click="$emit('submit')"
          :loading="submitting"
          :disabled="selectedItems.length === 0 && newItems.length === 0"
        >
          <v-icon left>mdi-check</v-icon>
          Continue with Selected Items ({{ selectedItems.length + newItems.length }})
        </v-btn>
        <v-btn outlined @click="$emit('cancel')" :disabled="submitting" class="ml-2">
          <v-icon left>mdi-close</v-icon>
          Cancel
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cr-manual-selection {
  padding: 20px;
}
.selection-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.selection-controls {
  display: flex;
  justify-content: center;
}
.selection-items-container {
  text-align: left;
}
.selection-item {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}
.selection-item:hover {
  background-color: #f5f5f5;
}
.selection-item.selected {
  border-color: #1976D2;
  background-color: #E3F2FD;
}
.selection-item.editing {
  border-color: #FF9800;
  background-color: #FFF3E0;
}
.selection-item.new-item {
  border-color: #4CAF50;
  background-color: #E8F5E9;
}
.selection-item-checkbox {
  flex-shrink: 0;
  margin-right: 12px;
  padding-top: 2px;
}
.selection-item-content {
  flex-grow: 1;
}
.selection-item-field {
  margin-bottom: 4px;
}
.selection-item-field-edit {
  margin-bottom: 8px;
}
.selection-item-actions {
  flex-shrink: 0;
  margin-left: 8px;
  display: flex;
  gap: 4px;
}
.selection-actions {
  display: flex;
  justify-content: center;
}
.selection-item-json {
  font-size: 12px;
  white-space: pre-wrap;
}
</style>
